import { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../components/GlobalContext";
import {
  initialUserState,
  LayoutState,
  LocalStorageKeys,
  UserState,
} from "../types";
import useUser from "./user";

const useLayout = () => {
  const context = useContext(GlobalContext);
  return [context.layout, context.setLayout] as const;
};

export const useGetStoredSettings = () => {
  const [, setLayout] = useLayout();
  const [, setUser] = useUser();
  useEffect(() => {
    const onLocalStorageChanged = () => {
      const rawSettings = localStorage.getItem(
        LocalStorageKeys.LAYOUT_SETTINGS
      );
      const settings = rawSettings
        ? (JSON.parse(rawSettings) as Partial<LayoutState["settings"]>)
        : {};

      if (settings.textSpeed) {
        setLayout((prevState) => ({
          ...prevState,
          settings: { ...prevState.settings, textSpeed: settings.textSpeed! },
        }));
      }

      if (settings.textColor) {
        setLayout((prevState) => ({
          ...prevState,
          settings: {
            ...prevState.settings,
            textColor: settings.textColor!,
          },
        }));
      }

      if (settings.textBackgroundColor) {
        setLayout((prevState) => ({
          ...prevState,
          settings: {
            ...prevState.settings,
            textBackgroundColor: settings.textBackgroundColor!,
          },
        }));
      }

      if (settings.textSize) {
        setLayout((prevState) => ({
          ...prevState,
          settings: {
            ...prevState.settings,
            textSize: settings.textSize!,
          },
        }));
      }

      const storedVoiceId = localStorage.getItem(LocalStorageKeys.VOICE_AVATAR);

      const aiVoiceGender = storedVoiceId
        ? storedVoiceId.split(" ")[0]
        : initialUserState.metadata.aiVoiceGender;
      const aiVoice = storedVoiceId
        ? storedVoiceId.split(" ")[1]
        : initialUserState.metadata.aiVoice;
      setUser((prevState) => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          aiVoiceGender:
            aiVoiceGender as UserState["metadata"]["aiVoiceGender"],
          aiVoice: aiVoice as UserState["metadata"]["aiVoice"],
        },
      }));
    };

    window.addEventListener("storage", onLocalStorageChanged);

    return () => {
      window.removeEventListener("storage", onLocalStorageChanged);
    };
  }, [setLayout, setUser]);
};

export const useWindowSize = () => {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { width, height };
};

export const useIsMobile = () => {
  const { width } = useWindowSize();
  return useMemo(() => {
    return width <= 1200;
  }, [width]);
};

export default useLayout;
